// ActionCable utilities for real-time features

export interface NotificationHandlers {
  onNew: (notification: any) => void;
  onCountUpdate: (count: number) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
}

export interface ConversationHandlers {
  onNewMessage: (message: any) => void;
  onMessageRead?: (data: { message_id: string; read_at: string }) => void;
  onTyping?: (data: { user_id: string; is_typing: boolean }) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
}

let consumer: any = null;

async function getConsumer() {
  if (consumer) return consumer;

  try {
    const { createConsumer } = await import('@rails/actioncable');
    consumer = createConsumer();
    return consumer;
  } catch (error) {
    console.error('Failed to create ActionCable consumer:', error);
    return null;
  }
}

export async function subscribeToNotifications(handlers: NotificationHandlers) {
  const cable = await getConsumer();
  if (!cable) return null;

  return cable.subscriptions.create(
    { channel: 'NotificationChannel' },
    {
      received(data: any) {
        switch (data.type) {
          case 'new_notification':
            handlers.onNew(data.notification);
            break;
          case 'notification_count':
            handlers.onCountUpdate(data.unread_count);
            break;
        }
      },
      connected() {
        handlers.onConnected?.();
      },
      disconnected() {
        handlers.onDisconnected?.();
      }
    }
  );
}

export async function subscribeToConversation(
  conversationId: string,
  handlers: ConversationHandlers
) {
  const cable = await getConsumer();
  if (!cable) return null;

  return cable.subscriptions.create(
    { channel: 'ConversationChannel', conversation_id: conversationId },
    {
      received(data: any) {
        switch (data.type) {
          case 'new_message':
            handlers.onNewMessage(data.message);
            break;
          case 'message_read':
            handlers.onMessageRead?.(data);
            break;
          case 'typing':
            handlers.onTyping?.(data);
            break;
        }
      },
      connected() {
        handlers.onConnected?.();
      },
      disconnected() {
        handlers.onDisconnected?.();
      },
      // Actions
      typing(isTyping: boolean) {
        this.perform('typing', { is_typing: isTyping });
      },
      markRead() {
        this.perform('mark_read');
      }
    }
  );
}

export async function subscribeToPresence(handlers: {
  onStatusChange: (data: { user_id: string; status: 'online' | 'offline'; last_seen_at: string }) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
}) {
  const cable = await getConsumer();
  if (!cable) return null;

  return cable.subscriptions.create(
    { channel: 'PresenceChannel' },
    {
      received(data: any) {
        handlers.onStatusChange(data);
      },
      connected() {
        handlers.onConnected?.();
      },
      disconnected() {
        handlers.onDisconnected?.();
      }
    }
  );
}
